import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Button,
  Select,
  MenuItem,
  TextField,
  Typography,
  Autocomplete,
  CircularProgress
} from '@mui/material';
import { competitionAccessService } from '../../services/competitionAccess';
import { supabase } from '../../services/supabase';

const ROLES = {
  owner: 'Owner',
  admin: 'Admin',
  editor: 'Editor',
  viewer: 'Viewer'
};

const CompetitionAccess = ({ competitionId }) => {
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('competition_access')
        .select(`
          *,
          users:user_id (
            id,
            email
          )
        `)
        .eq('competition_id', competitionId);

      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAllUsers = async () => {
    try {
      setSearchLoading(true);
      const { data, error } = await supabase
        .from('users')
        .select('id, email')
        .order('email');

      if (error) throw error;
      setAllUsers(data || []);
    } catch (error) {
      console.error('Error loading all users:', error);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleAddUser = async () => {
    if (!selectedUser) return;

    try {
      await competitionAccessService.grantAccess(
        competitionId,
        selectedUser.id,
        'viewer' // Default role
      );

      await loadUsers();
      setSelectedUser(null);
    } catch (error) {
      console.error('Error adding user:', error);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await competitionAccessService.updateAccess(
        competitionId,
        userId,
        newRole
      );
      loadUsers();
    } catch (error) {
      console.error('Error updating role:', error);
    }
  };

  const handleRemoveUser = async (userId) => {
    try {
      await competitionAccessService.revokeAccess(competitionId, userId);
      loadUsers();
    } catch (error) {
      console.error('Error removing user:', error);
    }
  };

  useEffect(() => {
    loadUsers();
    loadAllUsers();
  }, [competitionId]);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Manage Access
      </Typography>
      
      <Box sx={{ mb: 3, display: 'flex', gap: 1, alignItems: 'center' }}>
        <Autocomplete
          size="small"
          sx={{ width: 300 }}
          options={allUsers.filter(u => !users.some(existing => existing.user_id === u.id))}
          getOptionLabel={(option) => option.email || ''}
          value={selectedUser}
          onChange={(_, newValue) => setSelectedUser(newValue)}
          loading={searchLoading}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search users by email"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {searchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        <Button
          variant="contained"
          onClick={handleAddUser}
          disabled={!selectedUser}
        >
          Add User
        </Button>
      </Box>

      <Paper>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.user_id}>
                <TableCell>{user.users.email}</TableCell>
                <TableCell>
                  <Select
                    size="small"
                    value={user.role}
                    onChange={(e) => handleRoleChange(user.user_id, e.target.value)}
                  >
                    {Object.entries(ROLES).map(([value, label]) => (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </TableCell>
                <TableCell>
                  <Button
                    size="small"
                    color="error"
                    onClick={() => handleRemoveUser(user.user_id)}
                    disabled={user.role === 'owner'}
                  >
                    Remove
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export default CompetitionAccess;
